// Pelvis (inc Tailbone) Template Configuration
// Questions simplified to 12-year-old reading level

export const pelvisTemplate = {
  id: 'PELVIS',
  name: 'Pelvis (inc Tailbone)',
  icon: '🦴',
  dominantSide: 'none',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which side is the problem on?',
      options: ['Right', 'Left', 'Both', 'Centre over pubic bone'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did your pelvis problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Gradually', 'After childbirth', 'After a fall or impact', 'After a fall onto the tailbone'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Front pelvis pain',
        'Groin pain',
        'Back pelvis / SI joint pain',
        'Inner thigh pain that spreads',
        'Pain with kicking or running',
        'Pain with pivoting or changing direction',
        'Clicking or grinding feeling',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened in your own words:',
      placeholder: 'Describe how your pelvis problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly do you feel pain? (tick all that apply)',
      options: [
        'Pubic bone (centre)',
        'Groin (right)',
        'Groin (left)',
        'Adductor / inner thigh',
        'Lower abdomen',
        'SI joint (back of pelvis)',
        'Buttock',
        'Coccyx / tailbone'
      ]
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like? (tick all that apply)',
      options: ['Sharp', 'Aching', 'Stabbing', 'Bruised feeling', 'Other']
    },

    painCharacterOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painCharacter', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painWithKicking: {
      type: 'radio',
      label: 'Does it hurt when you kick?',
      options: ['Yes', 'No', 'I don\'t kick']
    },

    painWithRunning: {
      type: 'radio',
      label: 'Does it hurt when you run?',
      options: ['Yes', 'No']
    },

    painWithSitToStand: {
      type: 'radio',
      label: 'Does it hurt when you stand up from sitting?',
      options: ['Yes', 'No']
    },

    painWithCoughOrSneeze: {
      type: 'radio',
      label: 'Does it hurt when you cough or sneeze?',
      options: ['Yes', 'No']
    },

    painWithSingleLegStance: {
      type: 'radio',
      label: 'Does it hurt when you stand on one leg?',
      options: ['Yes', 'No']
    },

    painAtNight: {
      type: 'radio',
      label: 'Does the pain wake you up at night?',
      options: ['Yes', 'No']
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse? (tick all that apply)',
      options: [
        'Sprinting',
        'Kicking',
        'Twisting or changing direction',
        'Sit-ups or core exercises',
        'Stairs',
        'Walking',
        'Sitting for a long time',
        'Standing for a long time',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better? (tick all that apply)',
      options: [
        'Rest',
        'Ice',
        'Heat',
        'Pain medication',
        'Gentle stretching',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Sport Section
    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., football, rugby, running, gym'
    },

    kickingDominance: {
      type: 'radio',
      label: 'Which leg do you kick with?',
      options: ['Right', 'Left', 'Both']
    },

    recentTrainingChange: {
      type: 'radio',
      label: 'Have you recently changed how much or how hard you train?',
      options: ['Yes', 'No']
    },

    groinPainHistory: {
      type: 'radio',
      label: 'Have you had groin pain before?',
      options: ['Yes', 'No']
    },

    previousHernia: {
      type: 'radio',
      label: 'Have you ever had a hernia or hernia surgery?',
      options: ['Yes', 'No']
    },

    // Female-Specific Section
    recentChildbirth: {
      type: 'radio',
      label: 'Have you recently had a baby?',
      options: ['Yes, within the last 12 months', 'Yes, more than 12 months ago', 'No', 'Not applicable']
    },

    pregnancyRelatedPain: {
      type: 'radio',
      label: 'Did this pain start during or after pregnancy?',
      options: ['Yes', 'No']
    },

    menstrualStatus: {
      type: 'radio',
      label: 'If you have periods, how are they?',
      options: ['Regular', 'Irregular', 'No periods for 3+ months', 'Not applicable', 'Prefer not to say']
    },

    // Red Flags (cauda equina)
    urinaryDysfunction: {
      type: 'radio',
      label: 'Do you have any new trouble controlling your bladder?',
      options: ['Yes', 'No']
    },

    bowelDysfunction: {
      type: 'radio',
      label: 'Do you have any new trouble controlling your bowels?',
      options: ['Yes', 'No']
    },

    saddleAnesthesia: {
      type: 'radio',
      label: 'Do you have any numbness in your bottom or between your legs?',
      options: ['Yes', 'No']
    },

    unableToWeightBear: {
      type: 'radio',
      label: 'Are you unable to put weight on your leg?',
      options: ['Yes', 'No']
    },

    highEnergyTrauma: {
      type: 'radio',
      label: 'Was this caused by a hard impact (e.g., car crash, bad fall)?',
      options: ['Yes', 'No']
    },

    feverChills: {
      type: 'radio',
      label: 'Do you have a fever or chills?',
      options: ['Yes', 'No']
    },

    unexplainedWeightLoss: {
      type: 'radio',
      label: 'Have you lost weight without trying?',
      options: ['Yes', 'No']
    },

    historyOfCancer: {
      type: 'radio',
      label: 'Have you ever been told you have cancer?',
      options: ['Yes', 'No']
    },

    osteoporosis: {
      type: 'radio',
      label: 'Have you been told you have osteoporosis (weak bones)?',
      options: ['Yes', 'No']
    },

    // Treatment Section
    medications: {
      type: 'textarea',
      label: 'What medications or painkillers have you tried for this problem?',
      placeholder: 'List any medications you have taken',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any imaging (X-ray, MRI, ultrasound)?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'text',
      label: 'What type of imaging did you have?',
      placeholder: 'e.g., X-ray, MRI, ultrasound',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhere: {
      type: 'text',
      label: 'Where was the imaging done?',
      placeholder: 'e.g., Waikato Hospital, Pacific Radiology',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhen: {
      type: 'text',
      label: 'When was the imaging done?',
      placeholder: 'e.g., last week, 2 months ago',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What were the results? (if you know)',
      placeholder: 'e.g., normal, stress fracture, inflammation',
      rows: 2,
      showWhen: { field: 'imaging', includes: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Pelvis Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painCharacter', 'painCharacterOther', 'painSeverity', 'painWithKicking', 'painWithRunning', 'painWithSitToStand', 'painWithCoughOrSneeze', 'painWithSingleLegStance', 'painAtNight', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'sport',
      title: '4. Your Sport & Training',
      component: 'GenericSection',
      enabled: true,
      fields: ['sport', 'kickingDominance', 'recentTrainingChange', 'groinPainHistory', 'previousHernia']
    },
    {
      id: 'redFlags',
      title: '5. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'urinaryDysfunction', label: 'New bladder problems', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'bowelDysfunction', label: 'New bowel problems', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'saddleAnesthesia', label: 'Numbness in bottom or between legs', weight: 10, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'unableToWeightBear', label: 'Unable to put weight on leg', weight: 6, urgent: true, type: 'radio', options: ['Yes', 'No'] },
        { id: 'highEnergyTrauma', label: 'High-energy impact (e.g., car crash, bad fall)', weight: 6, type: 'radio', options: ['Yes', 'No'] },
        { id: 'feverChills', label: 'Fever or chills', weight: 4, type: 'radio', options: ['Yes', 'No'] },
        { id: 'unexplainedWeightLoss', label: 'Unexplained weight loss', weight: 5, type: 'radio', options: ['Yes', 'No'] },
        { id: 'historyOfCancer', label: 'History of cancer', weight: 5, type: 'radio', options: ['Yes', 'No'] },
        { id: 'osteoporosis', label: 'Osteoporosis (weak bones)', weight: 2, type: 'radio', options: ['Yes', 'No'] }
      ],
      alerts: {
        critical: { threshold: 10, message: 'URGENT: These symptoms need immediate medical attention' },
        urgent: { threshold: 6, message: 'Please contact us promptly about these symptoms' },
        concerning: { threshold: 5, message: 'Please mention these symptoms at your appointment' }
      }
    },
    {
      id: 'femaleSpecific',
      title: '6. Pregnancy & Hormonal Health',
      component: 'GenericSection',
      enabled: true,
      fields: ['recentChildbirth', 'pregnancyRelatedPain', 'menstrualStatus']
    },
    {
      id: 'treatment',
      title: '7. Treatment So Far',
      component: 'GenericSection',
      enabled: true,
      fields: ['medications', 'imaging', 'imagingType', 'imagingWhere', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'medical',
      title: '8. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'exerciseVitalSign',
      title: '9. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    {
      id: 'goals',
      title: '10. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Gradually': 'gradual onset',
      'After childbirth': 'onset post-partum',
      'After a fall or impact': 'onset following trauma',
      'After a fall onto the tailbone': 'onset following direct coccygeal impact'
    },
    commonDiagnoses: [
      'Osteitis pubis',
      'Adductor-related groin pain',
      'Adductor tendinopathy',
      'Iliopsoas-related groin pain',
      'Inguinal-related groin pain (sports hernia)',
      'Pubic ramus stress fracture',
      'SI joint dysfunction',
      'Athletic pubalgia',
      'Hip-related groin pain (consider FAI / labral)',
      'Pregnancy-related pelvic girdle pain',
      'Coccydynia',
      'Coccyx fracture'
    ]
  }
};

export default pelvisTemplate;
